import React, { useEffect, useState, useMemo } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { FaDownload } from 'react-icons/fa';
import myImage from '../my2.png';
import './Hero.css';

const Hero = () => {
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  
  const fullTexts = useMemo(() => ["Software Engineer", "Web Developer"], []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const fullText = fullTexts[currentIndex];

  // Talk box messages
  const talkMessages = useMemo(() => [
    "Welcome to my Portfolio",
    "I am free to hire",
    "Let's build together",
    "Available for projects"
  ], []);
  const [talkText, setTalkText] = useState('');
  const [talkIsDeleting, setTalkIsDeleting] = useState(false);
  const [talkIndex, setTalkIndex] = useState(0);
  
  const controls = useAnimation();

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isDeleting && text === fullText) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && text === '') {
        setIsDeleting(false);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % fullTexts.length);
        setLoopNum(loopNum + 1);
      } else {
        setText(isDeleting 
          ? fullText.substring(0, text.length - 1)
          : fullText.substring(0, text.length + 1)
        );
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [text, isDeleting, fullText, loopNum, fullTexts]);

  // Talk box typing animation
  useEffect(() => {
    const currentMessage = talkMessages[talkIndex];
    const timeout = setTimeout(() => {
      if (!talkIsDeleting && talkText === currentMessage) {
        // Wait before deleting
        setTimeout(() => setTalkIsDeleting(true), 2500);
      } else if (talkIsDeleting && talkText === '') {
        // Move to next message
        setTalkIsDeleting(false);
        setTalkIndex((prevIndex) => (prevIndex + 1) % talkMessages.length);
      } else {
        // Type or delete
        setTalkText(talkIsDeleting 
          ? currentMessage.substring(0, talkText.length - 1)
          : currentMessage.substring(0, talkText.length + 1)
        );
      }
    }, talkIsDeleting ? 50 : 80);

    return () => clearTimeout(timeout);
  }, [talkText, talkIsDeleting, talkIndex, talkMessages]);

  // Continuous floating animation
  useEffect(() => {
    const startAnimation = () => {
      controls.start({
        y: [0, -20, 0],
        rotate: [0, 5, -5, 0],
        transition: {
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }
      });
    };
    
    startAnimation();
  }, [controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section id="home" className="hero">
      <div className="hero-background">
        <div className="floating-shapes">
          <motion.div
            className="shape shape-1"
            variants={floatingVariants}
            animate="animate"
          />
          <motion.div
            className="shape shape-2"
            variants={floatingVariants}
            animate="animate"
            transition={{ delay: 1 }}
          />
          <motion.div
            className="shape shape-3"
            variants={floatingVariants}
            animate="animate"
            transition={{ delay: 2 }}
          />
        </div>
      </div>

      <motion.div
        className="hero-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="hero-content"
          variants={itemVariants}
        >
          <div className="hero-image">
              <div className="image-container">
                <div className="image-wrapper">
                  <img 
                    src={myImage} 
                    alt="Ransa Nilakshaya Abeywickrama (Ransa N Abeywickrama, rnabeywickrama) - Full Stack Developer & Freelancer available for hire"
                    className="hero-photo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="talk-box">
                    <span className="talk-box-text">{talkText}</span>
                    <span className="talk-box-cursor">|</span>
                  </div>
                  <div
                    className="image-placeholder"
                    style={{ display: 'none' }}
                  >
                    <span className="photo-text">
                      Developer
                    </span>
                  </div>
                </div>
            </div>
          </div>

          <motion.div
            className="hero-text"
            variants={itemVariants}
          >
            <motion.h1
              className="hero-title"
              variants={itemVariants}
            >
              Hi, I'm <span className="highlight">Ransa N Abeywickrama</span>
            </motion.h1>
            
            <motion.div
              className="hero-subtitle"
              variants={itemVariants}
            >
              I'm a <span className="typing-text">{text}</span>
              <span className="cursor">|</span>
            </motion.div>

            <motion.p
              className="hero-description"
              variants={itemVariants}
            >
              Passionate Full Stack Developer with expertise in building dynamic and responsive web applications.
              I love bringing ideas to life through clean, efficient, and scalable code. Specializing in modern
              JavaScript frameworks and cloud technologies. Available for freelance projects and ready to help bring your vision to reality.
            </motion.p>

            <motion.div
              className="hero-buttons"
              variants={itemVariants}
            >
              <motion.a
                href="#projects"
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View My Work
              </motion.a>
              <motion.a
                href="#contact"
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Hire Me
              </motion.a>
              <motion.a
                href="/Ransa-Nilakshaya-Abewickrama.pdf"
                download="Ransa-Nilakshaya-Abewickrama.pdf"
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaDownload /> Download CV
              </motion.a>
            </motion.div>

          </motion.div>
        </motion.div>

        <motion.div
          className="scroll-indicator"
          variants={itemVariants}
          animate={{
            y: [0, 10, 0],
            opacity: [1, 0.5, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="scroll-arrow">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p>Scroll Down</p>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
